// Text models through the Depth API proxy (ANTHROPIC.md, OPENAI.md §2, SERVER.md §2): the model id picks the
// provider (Claude ids → Anthropic, OpenAI ids → OpenAI, everything else → OpenRouter) and the request is translated
// exactly like the apps do it.
import { account } from "../api/account";
import { api, errorBody, problem, proxyBase, type Provider } from "../api/client";
import { modelNotAllowedMessage } from "../models";

export type TextProvider = "openrouter" | "anthropic" | "openai";
export type Effort = "low" | "medium";

export const isClaude = (m: string) => m.startsWith("claude-") && !m.includes("/");
const oDigit = (m: string) => /^o[0-9]/.test(m);
export const isOpenAI = (m: string) => !m.includes("/") && (m.startsWith("gpt-") || m.startsWith("chatgpt-") || oDigit(m));
export const isReasoning = (m: string) => !m.includes("chat") && (m.startsWith("gpt-5") || m.startsWith("gpt-6") || oDigit(m));
export const providerOf = (m: string): TextProvider => (isClaude(m) ? "anthropic" : isOpenAI(m) ? "openai" : "openrouter");
export const providerLabel = (p: TextProvider) => (p === "anthropic" ? "Claude" : p === "openai" ? "OpenAI" : "OpenRouter");
const providerName = (p: TextProvider) => (p === "anthropic" ? "Anthropic" : p === "openai" ? "OpenAI" : "OpenRouter");

export class AIError extends Error {
  constructor(message: string, readonly kind: "noKey" | "signedOut" | "notOnServer" | "invalidKey" | "rateLimited" | "http" | "badResponse" | "refused" | "credits" | "notAllowed",
    readonly status = 0) { super(message); }
  get isNoKey() { return this.kind === "noKey" || this.kind === "signedOut" || this.kind === "notOnServer"; }
  get isRetryable() { return this.kind === "rateLimited" || (this.kind === "http" && (this.status >= 500 || this.status === 408)); }
}

export function unavailable(model: string): string | null {
  return account.unavailableReason(providerOf(model) as Provider);
}

function httpError(p: TextProvider, status: number, json: any, text: string): AIError {
  const pr = problem(status, json);
  if (pr?.kind === "signedOut") return new AIError("Signed out. Sign in to use this (Settings → Account).", "signedOut", 401);
  if (pr?.kind === "providerUnavailable") return new AIError(`${providerName(p)} isn't available on this server.`, "notOnServer", 503);
  if (pr?.kind === "rateLimited") return new AIError(`${providerName(p)} is rate-limiting requests. Try again in a moment.`, "rateLimited", 429);
  if (status === 403 && json?.error === "model_not_allowed") return new AIError(modelNotAllowedMessage(json.model), "notAllowed", 403);
  const message = (json?.error?.message ?? text).toString().slice(0, 200);
  if (status === 401 || status === 403) return new AIError(`${providerName(p)} rejected the server's API key.`, "invalidKey", status);
  if (status === 429) {
    if (p === "openai" && json?.error?.code === "insufficient_quota") return new AIError("OpenAI credits used up.", "credits", 429);
    return new AIError(`${providerName(p)} is rate-limiting requests. Try again in a moment.`, "rateLimited", 429);
  }
  if (p === "anthropic" && (status === 529 || status >= 500)) return new AIError(`Claude is overloaded or unavailable (${status}). ${message}`, "http", status);
  return new AIError(`${providerName(p)} error ${status}. ${message}`, "http", status);
}

/** Lines of an SSE (or any text) stream. */
async function* lines(res: Response, signal?: AbortSignal): AsyncGenerator<string> {
  const reader = res.body!.getReader();
  const dec = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i;
      while ((i = buf.indexOf("\n")) >= 0) { yield buf.slice(0, i).replace(/\r$/, ""); buf = buf.slice(i + 1); }
    }
    if (buf) yield buf;
  } finally {
    reader.releaseLock();
  }
}

export interface CompleteOptions {
  json?: boolean; maxTokens?: number; temperature?: number; effort?: Effort; cachePrefix?: string | null;
  onDelta?: (text: string) => void; signal?: AbortSignal; timeoutMs?: number;
}

function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const t = AbortSignal.timeout(ms);
  return signal ? AbortSignal.any([signal, t]) : t;
}

// ---- Anthropic (ANTHROPIC.md §3)

const supportsEffort = (m: string) => !(m.includes("haiku") || m.startsWith("claude-3"))
  && !["claude-sonnet-4-5", "claude-opus-4-1", "claude-opus-4-0", "claude-sonnet-4-0"].some((p) => m.startsWith(p));
const usesFallbacks = (m: string) => ["claude-opus-5-5", "claude-opus-5", "claude-sonnet-5-5", "claude-fable-5-1"].includes(m);

export function anthropicBody(model: string, messages: { role: string; content: string }[], maxTokens: number, effort: Effort | null,
  stream: boolean, cachePrefix?: string | null): any {
  const system: string[] = [];
  const out: any[] = [];
  let cached = false;
  for (const m of messages) {
    if (m.role === "system") { system.push(m.content); continue; }
    let blocks: any[] = [{ type: "text", text: m.content }];
    if (!cached && m.role === "user" && cachePrefix && m.content.startsWith(cachePrefix) && m.content.length > cachePrefix.length) {
      blocks = [{ type: "text", text: cachePrefix, cache_control: { type: "ephemeral" } }, { type: "text", text: m.content.slice(cachePrefix.length) }];
      cached = true;
    }
    out.push({ role: m.role === "assistant" ? "assistant" : "user", content: blocks });
  }
  const body: any = { model, max_tokens: maxTokens + 8000, messages: out };
  if (system.length) body.system = system.join("\n\n");
  if (stream) body.stream = true;
  if (effort && supportsEffort(model)) body.output_config = { effort };
  if (usesFallbacks(model)) body.fallbacks = "default";
  return body;
}

function anthropicHeaders(model: string): Record<string, string> {
  const h: Record<string, string> = { "anthropic-version": "2023-06-01" };
  if (usesFallbacks(model)) h["anthropic-beta"] = "server-side-fallback-2026-07-01";
  return h;
}

// ---- OpenAI (OPENAI.md §2)

export function openAIBody(model: string, messages: { role: string; content: string }[], maxTokens: number, effort: Effort | null,
  stream: boolean, responseFormat?: any): any {
  const reasoning = isReasoning(model);
  const body: any = { model, messages: messages.map((m) => ({ role: m.role, content: m.content })),
    max_completion_tokens: reasoning ? maxTokens + 8000 : maxTokens };
  if (reasoning && effort) body.reasoning_effort = effort;
  if (stream) { body.stream = true; body.stream_options = { include_usage: true }; }
  if (responseFormat?.type === "json_object") {
    if (messages.some((m) => /json/i.test(m.content))) body.response_format = responseFormat;
  } else if (responseFormat?.type === "json_schema") {
    const f = { ...responseFormat, json_schema: { ...responseFormat.json_schema } };
    delete f.json_schema.strict;
    body.response_format = f;
  }
  return body;
}

// ---- One client

export class TextClient {
  constructor(readonly model: string) {}
  get provider(): TextProvider { return providerOf(this.model); }

  private check(): void {
    const why = unavailable(this.model);
    if (why) throw new AIError(why, account.signedIn ? "notOnServer" : "noKey");
  }

  /** One system + user prompt, streamed (onDelta gets the text so far). */
  async complete(system: string, user: string, o: CompleteOptions = {}): Promise<string> {
    this.check();
    const maxTokens = o.maxTokens ?? 2000, effort = o.effort ?? "low";
    const messages = [{ role: "system", content: system }, { role: "user", content: user }];
    const signal = withTimeout(o.signal, o.timeoutMs ?? 90_000);
    const p = this.provider;
    let res: Response;
    if (p === "anthropic") {
      res = await api(`${proxyBase("anthropic")}/v1/messages`, { method: "POST", signal, headers: anthropicHeaders(this.model),
        json: anthropicBody(this.model, messages, maxTokens, effort, true, o.cachePrefix) });
    } else if (p === "openai") {
      let body = openAIBody(this.model, messages, maxTokens, effort, true, o.json ? { type: "json_object" } : undefined);
      res = await api(`${proxyBase("openai")}/v1/chat/completions`, { method: "POST", signal, json: body });
      if (res.status === 400 && body.reasoning_effort) {
        const { text } = await errorBody(res.clone());
        if (text.includes("reasoning_effort")) {
          body = { ...body, max_completion_tokens: maxTokens };
          delete body.reasoning_effort;
          res = await api(`${proxyBase("openai")}/v1/chat/completions`, { method: "POST", signal, json: body });
        }
      }
    } else {
      const body: any = { model: this.model, messages, max_tokens: maxTokens, temperature: o.temperature ?? 0.3, stream: true,
        reasoning: { enabled: false } };
      if (o.json) body.response_format = { type: "json_object" };
      res = await api(`${proxyBase("openrouter")}/api/v1/chat/completions`, { method: "POST", signal, headers: { "X-Title": "Depth Reader" }, json: body });
    }
    if (!res.ok) { const { json, text } = await errorBody(res); throw httpError(p, res.status, json, text); }
    // A JSON (non-streamed) reply is accepted too.
    if (!(res.headers.get("content-type") || "").includes("event-stream")) {
      const obj = await res.json().catch(() => null);
      const text = p === "anthropic" ? (obj?.content ?? []).filter((b: any) => b.type === "text").map((b: any) => b.text).join("")
        : obj?.choices?.[0]?.message?.content;
      if (typeof text !== "string") throw new AIError("The model's answer couldn't be read. No message in the reply.", "badResponse");
      o.onDelta?.(text);
      return text;
    }
    let text = "", stop: string | null = null;
    for await (const line of lines(res, signal)) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") break;
      let obj: any;
      try { obj = JSON.parse(payload); } catch { continue; }
      if (p === "anthropic") {
        if (obj.type === "content_block_delta" && obj.delta?.type === "text_delta" && obj.delta.text) { text += obj.delta.text; o.onDelta?.(text); }
        else if (obj.type === "message_delta" && obj.delta?.stop_reason) stop = obj.delta.stop_reason;
        else if (obj.type === "message_stop") break;
        else if (obj.type === "error") {
          const kind = obj.error?.type ?? "";
          if (kind === "overloaded_error") throw new AIError(`Claude is overloaded or unavailable (529). ${obj.error?.message ?? ""}`, "http", 529);
          if (kind === "rate_limit_error") throw new AIError("Anthropic is rate-limiting requests. Try again in a moment.", "rateLimited", 429);
          throw new AIError(`Anthropic error 500. ${obj.error?.message ?? "Stream error"}`, "http", 500);
        }
      } else {
        if (obj.error) {
          if (obj.error.code === "insufficient_quota") throw new AIError("OpenAI credits used up.", "credits", 429);
          if (obj.error.code === "rate_limit_exceeded") throw new AIError("OpenAI is rate-limiting requests. Try again in a moment.", "rateLimited", 429);
          throw new AIError(`${providerName(p)} error ${obj.error.code ?? 500}. ${obj.error.message ?? "Stream error"}`, "http", typeof obj.error.code === "number" ? obj.error.code : 500);
        }
        const choice = obj.choices?.[0];
        const piece = choice?.delta?.content;
        if (typeof piece === "string" && piece) { text += piece; o.onDelta?.(text); }
        if (choice?.finish_reason) stop = choice.finish_reason;
      }
    }
    if (stop === "refusal" || stop === "content_filter") throw new AIError(p === "openai" ? "OpenAI declined to write this." : "Claude declined to write this.", "refused");
    if ((stop === "max_tokens" || stop === "length") && !text.trim()) throw new AIError("The model's answer couldn't be read. The model ran out of room.", "badResponse");
    return text;
  }

  /** A non-streamed request from an OpenRouter-style body (Ask, tagging) → the reply text. */
  async chat(body: any, o: { signal?: AbortSignal; timeoutMs?: number; effort?: Effort } = {}): Promise<string> {
    this.check();
    const p = this.provider;
    const signal = withTimeout(o.signal, o.timeoutMs ?? 60_000);
    const messages = body.messages as { role: string; content: string }[];
    const maxTokens = body.max_tokens ?? 2000;
    let res: Response;
    if (p === "anthropic") {
      res = await api(`${proxyBase("anthropic")}/v1/messages`, { method: "POST", signal, headers: anthropicHeaders(this.model),
        json: anthropicBody(this.model, messages, maxTokens, o.effort ?? "low", false) });
    } else if (p === "openai") {
      res = await api(`${proxyBase("openai")}/v1/chat/completions`, { method: "POST", signal,
        json: openAIBody(this.model, messages, maxTokens, o.effort ?? "low", false, body.response_format) });
    } else {
      res = await api(`${proxyBase("openrouter")}/api/v1/chat/completions`, { method: "POST", signal, headers: { "X-Title": "Depth" },
        json: { ...body, model: this.model } });
      if (res.status === 400) {
        const { text } = await errorBody(res.clone());
        if (text.toLowerCase().includes("reasoning")) {
          const plain = { ...body, model: this.model };
          delete plain.reasoning;
          res = await api(`${proxyBase("openrouter")}/api/v1/chat/completions`, { method: "POST", signal, headers: { "X-Title": "Depth" }, json: plain });
        }
      }
    }
    if (!res.ok) { const { json, text } = await errorBody(res); throw httpError(p, res.status, json, text); }
    const obj = await res.json().catch(() => null);
    const text = p === "anthropic" ? (obj?.content ?? []).filter((b: any) => b.type === "text").map((b: any) => b.text).join("")
      : obj?.choices?.[0]?.message?.content;
    if (typeof text !== "string" || !text.trim()) throw new AIError("The model's answer couldn't be read. No message in the reply.", "badResponse");
    return text;
  }
}
